import React, { useState, useEffect } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Typography, Avatar, IconButton, Chip, Grid,
    TextField, MenuItem, Switch, Button, Alert, CircularProgress,
} from "@mui/material";
import GavelIcon from "@mui/icons-material/Gavel";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import StorefrontIcon from "@mui/icons-material/Storefront";

import { LOYALTY_API_BASE, SECTION_COLORS } from "../../constants";
import { loyaltyFetch } from "../../hooks/loyaltyFetch";
import { useBrand } from "../../../../contexts/BrandContext";
import ConditionBuilder, { buildConditionsJson } from "./ConditionBuilder";
import ActionBuilder, { buildActionsArray } from "./ActionBuilder";

const INITIAL_FORM = {
    name: "",
    description: "",
    transaction_types: [],
    segment_ids: [],
    active: true,
};

const CreateRuleDialog = ({ open, onClose, onCreated }) => {
    const { currentBrand } = useBrand();
    const brandName = currentBrand?.name || "";

    const [form, setForm] = useState({ ...INITIAL_FORM });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [transactionTypeOptions, setTransactionTypeOptions] = useState([]);
    const [etLoading, setEtLoading] = useState(false);
    const [segmentOptions, setSegmentOptions] = useState([]);
    const [segmentsLoading, setSegmentsLoading] = useState(false);

    const [actionCatalog, setActionCatalog] = useState([]);
    const [conditionCatalog, setConditionCatalog] = useState([]);
    const [uiHints, setUiHints] = useState({});

    // Payload schema state
    const [hasPayloadSchema, setHasPayloadSchema] = useState(null); // null = unknown, true/false
    const [payloadPaths, setPayloadPaths] = useState([]);
    const [loadingSchema, setLoadingSchema] = useState(false);

    // ActionBuilder state: Array of { type, params }
    const [actionState, setActionState] = useState([]);

    // Condition builder state
    const [combinator, setCombinator] = useState("and");
    const [condRows, setCondRows] = useState([]);

    const handleConditionsChange = (newCombinator, newRows) => {
        setCombinator(newCombinator);
        setCondRows(newRows);
    };

    useEffect(() => {
        if (!open || !brandName) return;
        setEtLoading(true);
        const query = `?brand=${encodeURIComponent(brandName)}&active=true`;
        loyaltyFetch(`${LOYALTY_API_BASE}/admin/transaction-types${query}`, { headers: { "X-Brand": brandName } })
            .then((r) => r.ok ? r.json() : [])
            .then((data) => setTransactionTypeOptions(Array.isArray(data) ? data : []))
            .catch(() => setTransactionTypeOptions([]))
            .finally(() => setEtLoading(false));

        loyaltyFetch(`${LOYALTY_API_BASE}/admin/rule-conditions`, { headers: { "X-Brand": brandName } })
            .then(res => res.ok ? res.json() : {})
            .then(data => {
                setConditionCatalog(data.conditions || []);
                setUiHints(data.uiHints || {});
            })
            .catch(err => console.error("Failed to load conditions catalog", err));

        loyaltyFetch(`${LOYALTY_API_BASE}/admin/rule-actions`, { headers: { "X-Brand": brandName } })
            .then(res => res.ok ? res.json() : { actions: [] })
            .then(data => setActionCatalog(data.actions || []))
            .catch(err => console.error("Failed to load actions catalog", err));

        setSegmentsLoading(true);
        loyaltyFetch(`${LOYALTY_API_BASE}/admin/ui-options/segments${query}`, { headers: { "X-Brand": brandName } })
            .then((r) => r.ok ? r.json() : null)
            .then((data) => setSegmentOptions(Array.isArray(data?.items) ? data.items : []))
            .catch(() => setSegmentOptions([]))
            .finally(() => setSegmentsLoading(false));
    }, [open, brandName]);

    const primaryTransactionType = form.transaction_types?.[0] || "";

    // When selected transaction types change, derive payloadPaths from the first selected type.
    useEffect(() => {
        if (!primaryTransactionType) {
            setHasPayloadSchema(null);
            setPayloadPaths([]);
            return;
        }
        // Find the matching event type from the already loaded list
        const found = transactionTypeOptions.find((et) => et.key === primaryTransactionType);
        if (!found) {
            // Not in list yet (loading), try a direct fetch via admin/transaction-types filtered by key
            setPayloadPaths([]);
            setHasPayloadSchema(null);
            return;
        }
        if (found.payload_schema && typeof found.payload_schema === "object" && Object.keys(found.payload_schema).length > 0) {
            setHasPayloadSchema(true);
            setPayloadPaths(Object.keys(found.payload_schema));
        } else {
            setHasPayloadSchema(false);
            setPayloadPaths([]);
        }
    }, [primaryTransactionType, transactionTypeOptions]);

    const handle = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));

    const isValid = brandName && form.name.trim() && (form.transaction_types?.length || 0) > 0;

    const handleSubmit = async () => {
        if (!isValid) return;
        setSubmitting(true);
        setError(null);
        try {
            // Build conditions JSON from builder state
            const conditions = buildConditionsJson(combinator, condRows);

            // Build actions array from ActionBuilder state
            const actionsArr = buildActionsArray(actionState);

            const body = {
                brand: brandName,
                name: form.name.trim(),
                description: form.description.trim() || undefined,
                transaction_type: form.transaction_types[0],
                transaction_types: form.transaction_types,
                segment_ids: Array.isArray(form.segment_ids) && form.segment_ids.length > 0 ? form.segment_ids : null,
                conditions,
                actions: actionsArr,
                active: form.active,
            };
            const res = await loyaltyFetch(`${LOYALTY_API_BASE}/rules`, {
                method: "POST",
                headers: { "Content-Type": "application/json", "X-Brand": brandName },
                body: JSON.stringify(body),
            });
            if (!res.ok) {
                const e = await res.json().catch(() => null);
                throw new Error(e?.detail || `Erreur HTTP ${res.status}`);
            }
            onCreated();
            handleClose();
        } catch (err) { setError(err.message); } finally { setSubmitting(false); }
    };

    const handleClose = () => {
        setForm({ ...INITIAL_FORM });
        setActionState([]);
        setCombinator("and");
        setCondRows([]);
        setHasPayloadSchema(null);
        setPayloadPaths([]);
        setSegmentOptions([]);
        setError(null);
        onClose();
    };

    const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: 2 } };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ borderBottom: "2px solid #f1f3f5", px: 3, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 32, height: 32, background: SECTION_COLORS.rules.gradient }}>
                        <GavelIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Typography variant="h6" fontFamily="Montserrat, sans-serif" fontWeight={700} color="#1a1a2e" fontSize={16}>
                        Nouvelle Règle
                    </Typography>
                </Box>
                <IconButton onClick={handleClose} size="small"><CloseIcon /></IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: "24px !important", px: 3, pb: 2 }}>

                <Grid container spacing={2}>
                    {/* name & description */}
                    <Grid item xs={12}>
                        <TextField fullWidth required label="Nom de la règle" value={form.name}
                            onChange={handle("name")} size="small" disabled={!brandName}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth label="Description" value={form.description}
                            onChange={handle("description")} size="small" multiline rows={2} disabled={!brandName}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
                    </Grid>

                    {/* transaction_types dropdown */}
                    <Grid item xs={12}>
                        <TextField fullWidth required select
                            label={etLoading ? "Chargement des types…" : "Types de transaction"}
                            InputLabelProps={{ shrink: true }}
                            value={form.transaction_types}
                            onChange={(e) => {
                                const value = e.target.value;
                                setForm((p) => ({
                                    ...p,
                                    transaction_types: Array.isArray(value) ? value : [value].filter(Boolean),
                                }));
                            }}
                            size="small" disabled={!brandName || etLoading}
                            helperText={
                                !brandName ? "Sélectionnez une marque d'abord" :
                                    etLoading ? "Récupération en cours…" :
                                        transactionTypeOptions.length === 0
                                            ? "⚠️ Aucun type actif — créez-en dans Types de Transactions"
                                            : `${transactionTypeOptions.length} type(s) disponible(s) - sélection multiple`
                            }
                            sx={fieldSx}
                            SelectProps={{
                                multiple: true,
                                displayEmpty: true,
                                renderValue: (selected) => {
                                    const selectedKeys = Array.isArray(selected) ? selected : [];
                                    if (selectedKeys.length === 0) return "Sélectionnez un ou plusieurs types";
                                    return selectedKeys.join(", ");
                                },
                            }}>
                            {transactionTypeOptions.length === 0 && !etLoading && (
                                <MenuItem value="" disabled>Aucun type de transaction actif</MenuItem>
                            )}
                            {transactionTypeOptions.map((et) => (
                                <MenuItem key={et.id} value={et.key}>
                                    <Box>
                                        <Typography fontSize={13} fontWeight={600}>{et.name}</Typography>
                                        <Typography fontSize={11} color="text.secondary">{et.key}</Typography>
                                    </Box>
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    {/* segment_ids multi-select */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            select
                            label={segmentsLoading ? "Chargement des segments…" : "Segments ciblés (optionnel)"}
                            InputLabelProps={{ shrink: true }}
                            value={form.segment_ids}
                            onChange={(e) => {
                                const value = e.target.value;
                                setForm((p) => ({
                                    ...p,
                                    segment_ids: Array.isArray(value) ? value : [value].filter(Boolean),
                                }));
                            }}
                            size="small"
                            disabled={!brandName || segmentsLoading}
                            helperText={
                                !brandName ? "Sélectionnez une marque d'abord"
                                    : segmentOptions.length === 0 && !segmentsLoading
                                        ? "Aucun segment actif — créez-en dans l’onglet Segments"
                                        : `${segmentOptions.length} segment(s) disponible(s) - sélection multiple`
                            }
                            sx={fieldSx}
                            SelectProps={{
                                multiple: true,
                                displayEmpty: true,
                                renderValue: (selected) => {
                                    const ids = Array.isArray(selected) ? selected : [];
                                    if (ids.length === 0) return "Tous les clients (pas de ciblage par segment)";
                                    const byId = new Map(segmentOptions.map((s) => [String(s.id), s.name || String(s.id)]));
                                    return ids.map((id) => byId.get(String(id)) || String(id)).join(", ");
                                },
                            }}
                        >
                            {segmentOptions.length === 0 && !segmentsLoading && (
                                <MenuItem value="" disabled>Aucun segment actif</MenuItem>
                            )}
                            {segmentOptions.map((s) => (
                                <MenuItem key={s.id} value={String(s.id)}>
                                    <Box>
                                        <Typography fontSize={13} fontWeight={600}>{s.name}</Typography>
                                        <Typography fontSize={11} color="text.secondary">
                                            {s.is_dynamic ? "Dynamique" : "Statique"} • {s.id}
                                        </Typography>
                                    </Box>
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    {/* ── Condition Builder ── */}
                    <Grid item xs={12}>
                        <Typography fontSize={13} fontWeight={700} mb={1} color="#334155">
                            Conditions
                            {loadingSchema && <CircularProgress size={12} sx={{ ml: 1 }} />}
                        </Typography>
                        <ConditionBuilder
                            brandName={brandName}
                            transactionType={primaryTransactionType}
                            conditionCatalog={conditionCatalog}
                            uiHints={uiHints}
                            combinator={combinator}
                            rows={condRows}
                            onChange={handleConditionsChange}
                            hasPayloadSchema={hasPayloadSchema}
                            payloadPaths={payloadPaths}
                        />
                    </Grid>

                    {/* ── Action Builder ── */}
                    <Grid item xs={12}>
                        <ActionBuilder
                            brandName={brandName}
                            actionCatalog={actionCatalog}
                            value={actionState}
                            onChange={setActionState}
                        />
                    </Grid>

                    {/* active toggle */}
                    <Grid item xs={12}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" px={1.5} py={1}
                            sx={{ borderRadius: 2, bgcolor: "#fafbfd", border: "1px solid #e8ecf1" }}>
                            <Box>
                                <Typography fontSize={13} fontWeight={600} fontFamily="Montserrat, sans-serif">Activer</Typography>
                                <Typography fontSize={11} color="text.secondary">La règle sera appliquée immédiatement</Typography>
                            </Box>
                            <Switch checked={form.active} onChange={() => setForm((p) => ({ ...p, active: !p.active }))}
                                disabled={!brandName}
                                sx={{
                                    "& .MuiSwitch-switchBase.Mui-checked": { color: SECTION_COLORS.rules.main },
                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: SECTION_COLORS.rules.main }
                                }} />
                        </Box>
                    </Grid>
                </Grid>

                {error && <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{error}</Alert>}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e0e0e0", gap: 1 }}>
                <Button onClick={handleClose} sx={{ color: "text.secondary", textTransform: "none" }}>Annuler</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={!isValid || submitting}
                    startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                    sx={{
                        background: SECTION_COLORS.rules.gradient, color: "#fff", textTransform: "none", borderRadius: 2, fontWeight: 600, px: 3,
                        "&:hover": { boxShadow: `0 4px 16px ${SECTION_COLORS.rules.main}40` }
                    }}>
                    {submitting ? "Création..." : "Créer la Règle"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateRuleDialog;
